import { useEffect, useRef, useState } from "react";
import { Alert, Button, Container, OffcanvasBody, OffcanvasHeader } from "reactstrap";
import cx from "classnames";
import moment from "moment";
import styles from "./style.module.scss"
import DefaultOffCanvas, { DefaultOffCanvasProps } from "../DefaultOffCanvas";
import LoadingScreen from "@components/content/LoadingScreen";
import CreditAmountForm from "@components/forms/CreditAmountForm";
import { DynamicFormRef } from "@mavichpe/dynamic-forms";
import {
    CloseAddCreditAmountPubSub,
    CurrencyIdPubSub,
    OpenAddCreditAmountPubSub,
    OpenAddCreditAmountPubSubPayload
} from "@utils/pubsubs/PubSub";
import { showAlert } from "@utils/Alert";
import { omitProperties } from "@utils/Utils";
import { resolveCurrentUserLabel } from "@utils/Auth";
import {
    CREDIT_AMOUNT_TYPE_FIXED,
    distributeByBalance,
    resolveCreditAmount,
    validateCreditAmount
} from "@utils/CreditAmount";
import {
    CLIENT_CREDIT_ACTIVE_STATUS,
    INVOICE_PAYMENT_ACTIVE_STATUS,
    OTHER_PAYMENT_WAY_CODE
} from "@utils/Constants";
import { CreditAmountFormData } from "@/formTypes/CreditAmountFormDataType";
import { InvoicePaymentApi, InvoicePaymentDataType } from "@/api/InvoicePaymentApi";
import { SettlementPaymentApi, SettlementPaymentDataType } from "@/api/SettlementPaymentApi";
import { ClientCreditApi, ClientCreditDataType } from "@/api/ClientCreditApi";

type AddCreditAmountOffCanvasProps = {
    onCreditAmountCreated?: () => void,
} & DefaultOffCanvasProps

const EMPTY_PAYLOAD: OpenAddCreditAmountPubSubPayload = {
    clientId: "",
    clientNombre: "",
    documents: [],
    currency: {},
    date: ""
}

const AddCreditAmountOffCanvas = (props: AddCreditAmountOffCanvasProps) => {
    const { btnColor = "primary", className, onCreditAmountCreated } = props;
    const [isOpen, setOpen] = useState(false);
    const [isLoading, setLoading] = useState(false);
    const [creditData, setCreditData] = useState<OpenAddCreditAmountPubSubPayload>(EMPTY_PAYLOAD);
    const [creditDate, setCreditDate] = useState<string>("");
    const formRef = useRef<DynamicFormRef>(null);

    const { documents = [] } = creditData;
    const hasDocuments = documents.length > 0;
    const currencyId = Number(creditData.currency?.currency_id || 1);

    useEffect(() => {
        const unsubscribe = OpenAddCreditAmountPubSub.subscribe(openOffCanvas);
        const unsubscribeClose = CloseAddCreditAmountPubSub.subscribe(closeOffCanvas);
        return () => {
            unsubscribe();
            unsubscribeClose();
        };
    }, []);

    const closeOffCanvas = async () => {
        setOpen(false);
    }

    const openOffCanvas = async (payload: OpenAddCreditAmountPubSubPayload) => {
        const { openOffCanvas = false } = payload;
        setOpen(openOffCanvas);
        if (openOffCanvas) {
            setCreditData(payload)
        }
    }

    useEffect(() => {
        if (isOpen && creditData.clientId) {
            applyDefaultValues();
        }
    }, [isOpen, creditData]);

    const toogleOffCanvas = (newState: boolean) => {
        setOpen(newState);
        if (newState == false) {
            onCanvasClosed();
        }
    }

    const onCanvasClosed = () => {
        setOpen(false);
    }

    const applyDefaultValues = () => {
        const date = creditData.date || moment().format("YYYY-MM-DD");
        setCreditDate(date);
        if (creditData.currency?.id) {
            CurrencyIdPubSub.publish({ currencyId: `${creditData.currency.id}` })
        }
        formRef.current?.resetFormValue({
            fecha: date,
            tipo: CREDIT_AMOUNT_TYPE_FIXED,
            descripcion: "Saldo a favor",
            currencyId: creditData.currency?.id,
            exchange_rate: creditData.currency?.exchange_rate
        })
    }

    const onOffCanvasOpened = () => {
        applyDefaultValues();
    }

    const buildAlert = () => {
        if (documents.length === 1) {
            const [document] = documents;
            const documentLabel = document.kind === "settlement" ? "la liquidacion" : "la factura";
            return {
                color: "info",
                text: `Se registrara un abono sobre ${documentLabel} #${document.numero} por el monto indicado.
                    El documento quedara con saldo pendiente.`
            }
        }
        if (documents.length > 1) {
            return {
                color: "info",
                text: `Se registrara un abono en cada uno de los ${documents.length} documentos seleccionados,
                    repartiendo el monto de forma proporcional al saldo de cada uno.`
            }
        }
        return {
            color: "warning",
            text: `No hay facturas seleccionadas: se registrara un saldo a favor a nombre del cliente,
                que podra aplicarse despues al cancelar facturas.`
        }
    }

    const createDocumentPayments = async (formData: CreditAmountFormData, amount: number, user: string) => {
        const allocations = distributeByBalance(amount, documents);

        const paymentPromises = allocations.map(allocation => {
            const documentLabel = allocation.kind === "settlement" ? "Liquidacion" : "Factura";
            const paymentData = {
                monto: allocation.monto,
                vuelto: 0,
                estado: INVOICE_PAYMENT_ACTIVE_STATUS,
                formaPago: `${OTHER_PAYMENT_WAY_CODE}`,
                descripcion: `Saldo a favor - ${formData.descripcion} - ${documentLabel} #${allocation.numero}`,
                created: creditDate,
                currencyId: creditData.currency?.id,
                exchangeRate: Number(creditData.currency?.exchange_rate || 1),
                user
            };

            if (allocation.kind === "settlement") {
                const payment: SettlementPaymentDataType = omitProperties({
                    ...paymentData,
                    settlementId: allocation.id,
                }, [], true);
                return SettlementPaymentApi.create(payment);
            }

            const payment: InvoicePaymentDataType = omitProperties({
                ...paymentData,
                invoiceId: allocation.id,
            }, [], true);
            return InvoicePaymentApi.create(payment);
        });

        await Promise.all(paymentPromises);
    }

    const createClientCredit = async (formData: CreditAmountFormData, amount: number, user: string) => {
        const clientCredit: ClientCreditDataType = omitProperties({
            clientId: creditData.clientId,
            clientNombre: creditData.clientNombre,
            descripcion: `${formData.descripcion}`,
            monto: amount,
            aplicado: 0,
            estado: CLIENT_CREDIT_ACTIVE_STATUS,
            created: creditDate,
            currencyId: creditData.currency?.id,
            exchangeRate: Number(creditData.currency?.exchange_rate || 1),
            user
        }, [], true);

        return await ClientCreditApi.createClientCredit(clientCredit);
    }

    const onCreditAmountSubmit = async (formData?: CreditAmountFormData) => {
        if (!formData) {
            return;
        }

        const baseTotal = documents.reduce((a, d) => a + Number(d.saldo || 0), Number(0));
        const amount = resolveCreditAmount(`${formData.tipo}`, Number(formData.valor || 0), baseTotal);

        const validationError = validateCreditAmount(amount, hasDocuments ? baseTotal : 0);
        if (validationError) {
            showAlert("danger", validationError);
            return;
        }

        if (!creditData.clientId) {
            showAlert("danger", "No fue posible determinar el cliente para el monto a favor");
            return;
        }

        if (!creditDate) {
            showAlert("danger", "Debe indicar la fecha del monto a favor");
            return;
        }

        setLoading(true)
        try {
            const user = await resolveCurrentUserLabel();

            if (hasDocuments) {
                await createDocumentPayments(formData, amount, user);
                showAlert("success", `Se registro el monto a favor en ${documents.length} documento(s)`);
            } else {
                const generatedCredit = await createClientCredit(formData, amount, user);
                showAlert("success", `Se registro el saldo a favor #${generatedCredit.numero} del cliente`);
            }

            if (onCreditAmountCreated) {
                onCreditAmountCreated();
            }
            CloseAddCreditAmountPubSub.publish({});
        } catch (err) {
            if (err instanceof Error) {
                showAlert("danger", err.message);
            }
        } finally {
            setLoading(false)
        }
    }

    const alertData = buildAlert();

    return (
        <>
            {props.children && (<Button
                color={btnColor}
                type="button"
                className={cx([className, styles.button])}
                onClick={() => toogleOffCanvas(!isOpen)}
            >
                {props.children}
            </Button>
            )}
            <DefaultOffCanvas
                className={styles.offCanvas}
                direction="end"
                isOpen={isOpen}
                onOpened={onOffCanvasOpened}
                onClosed={onCanvasClosed}
                toggle={() => toogleOffCanvas(!isOpen)}
            >
                <OffcanvasHeader className={styles.offCanvasHeader} toggle={onCanvasClosed}>
                    {hasDocuments
                        ? `Agregar Monto a Favor a ${documents.length} documento(s)`
                        : "Agregar Saldo a Favor al Cliente"}
                </OffcanvasHeader>
                <OffcanvasBody>
                    <Container>
                        <Alert color={alertData.color} data-testid="credit-amount-alert">
                            {alertData.text}
                        </Alert>
                        <CreditAmountForm
                            ref={formRef}
                            documents={documents}
                            currencyId={currencyId}
                            onDateChange={(value) => setCreditDate(value)}
                            onCreditAmountSubmit={onCreditAmountSubmit}
                        />
                    </Container>
                </OffcanvasBody>
            </DefaultOffCanvas >
            <LoadingScreen isLoading={isLoading} />
        </>
    );
}
export default AddCreditAmountOffCanvas;
